import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { Toaster } from "@/components/ui/sonner";
import { Loader } from "@/components/Loader";
import Landing from "@/pages/Landing";
import Auth from "@/pages/Auth";
import AuthCallback from "@/pages/AuthCallback";
import Onboarding from "@/pages/Onboarding";
import Discover from "@/pages/Discover";
import EventDetail from "@/pages/EventDetail";
import Matches from "@/pages/Matches";
import CallRoom from "@/pages/CallRoom";
import SetupTest from "@/pages/SetupTest";
import Messages from "@/pages/Messages";
import Chat from "@/pages/Chat";
import Activity from "@/pages/Activity";
import Profile from "@/pages/Profile";
import AdminControl from "@/pages/AdminControl";
import PaymentSuccess, { PaymentCancel } from "@/pages/PaymentSuccess";
import Admin from "@/pages/Admin";
import Premium from "@/pages/Premium";
import { Terms, Privacy } from "@/pages/Legal";
import { UpgradeSheet } from "@/components/UpgradeSheet";
import { IncomingCallModal } from "@/components/IncomingCallModal";
import { AppLayout } from "@/components/AppLayout";

function Protected({ children, needOnboard = true }) {
  const { user, loading } = useAuth();
  const location = useLocation();
  if (loading || user === undefined) return <Loader />;
  if (!user) return <Navigate to="/login" replace />;
  if (needOnboard && !user.onboarded && location.pathname !== "/onboarding")
    return <Navigate to="/onboarding" replace />;
  return children;
}

function AdminOnly({ children }) {
  const { user, loading } = useAuth();
  if (loading || user === undefined) return <Loader />;
  if (!user) return <Navigate to="/login" replace />;
  if (user.role !== "admin") return <Navigate to="/discover" replace />;
  return children;
}

function Home() {
  const { user, loading } = useAuth();
  if (loading || user === undefined) return <Loader />;
  if (user) return <Navigate to="/discover" replace />;
  return <Landing />;
}

function GlobalOverlays() {
  const { user } = useAuth();
  if (!user) return null;
  return (<><UpgradeSheet /><IncomingCallModal /></>);
}

function AppRouter() {
  const location = useLocation();
  if (location.hash?.includes("session_id=")) return <AuthCallback />;
  return (
    <>
    <GlobalOverlays />
    <Routes>
      <Route path="/" element={<Home />} />
      <Route path="/login" element={<Auth />} />
      <Route path="/onboarding" element={<Protected needOnboard={false}><Onboarding /></Protected>} />
      <Route path="/call/:matchId" element={<Protected><CallRoom /></Protected>} />
      <Route path="/setup-test" element={<Protected><SetupTest /></Protected>} />
      <Route path="/premium" element={<Protected><Premium /></Protected>} />
      <Route path="/payment/success" element={<Protected><PaymentSuccess /></Protected>} />
      <Route path="/payment/cancel" element={<Protected><PaymentCancel /></Protected>} />
      <Route path="/terms" element={<Terms />} />
      <Route path="/privacy" element={<Privacy />} />
      <Route path="/chat/:matchId" element={<Protected><Chat /></Protected>} />
      <Route element={<Protected><AppLayout /></Protected>}>
        <Route path="/discover" element={<Discover />} />
        <Route path="/events/:id" element={<EventDetail />} />
        <Route path="/matches" element={<Matches />} />
        <Route path="/messages" element={<Messages />} />
        <Route path="/activity" element={<Activity />} />
        <Route path="/profile" element={<Profile />} />
      </Route>
      <Route path="/admin" element={<AdminOnly><Admin /></AdminOnly>} />
      <Route path="/admin/control" element={<AdminOnly><AdminControl /></AdminOnly>} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
    </>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <AppRouter />
        <Toaster position="top-center" theme="dark" richColors />
      </AuthProvider>
    </BrowserRouter>
  );
}
